import { type HTMLAttributes, type ReactNode } from 'react'

import { cn } from './cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Drops the default padding for cards that hold a full-bleed table. */
  flush?: boolean
}

/** `.card` — white, hairline border, 12px radius, `shadow-sm`. */
export function Card({ flush = false, className, children, ...rest }: CardProps) {
  return (
    <div {...rest} className={cn('card', flush ? 'p-0' : 'p-[18px]', className)}>
      {children}
    </div>
  )
}

export interface CardHeaderProps {
  /** The small uppercase brand-blue line above the title. */
  kicker?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Right-aligned controls on the title row. */
  actions?: ReactNode
  className?: string
}

/**
 * The header block the mockup repeats on every card: an optional kicker, an
 * `h5`-weight title, a muted one-line description, and actions pushed right.
 */
export function CardHeader({ kicker, title, description, actions, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        {kicker ? <div className="card-kicker">{kicker}</div> : null}
        <h5 className="m-0">{title}</h5>
        {description ? (
          <div className="mt-0.5 text-meta text-muted">{description}</div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/** A screen-level heading with the same kicker/title/description rhythm. */
export function ScreenHeader({ kicker, title, description, actions, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {kicker ? <div className="card-kicker">{kicker}</div> : null}
        <h2 className="mb-1 mt-0.5">{title}</h2>
        {description ? (
          <p className="m-0 max-w-[640px] text-body text-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/** The mockup's 132px label column. Renders a real `<dl>`. */
export function KeyValues({
  items,
  className,
}: {
  items: Array<{ label: ReactNode; value: ReactNode }>
  className?: string
}) {
  return (
    <dl className={cn('gw-kv m-0', className)}>
      {items.map((item, index) => (
        // Labels repeat across cards but not within one, and the list is
        // positional, so the index is the stable key here.
        <div key={index} className="contents">
          <dt>{item.label}</dt>
          <dd className="m-0">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}
